import { useEndpoint } from './useEndpoint';
import { useToastNotifications } from './useToastNotifications';
import {
  type ApiResponseBase,
  type MergeCandidate,
  type MergeContactsResponse,
  type MergePayload,
  type MergePreview,
} from '@apparatix/types/Contacts';

type ErrorResponse = { error?: string };

export const useContactMerge = () => {
  const { showError } = useToastNotifications();

  const showServerError = (fallback: string) => (response?: ErrorResponse) =>
    showError(response?.error ?? fallback, 'Error', 8000);

  const candidatesEndpoint = useEndpoint<ApiResponseBase<{ candidates: MergeCandidate[] }>, ErrorResponse>({
    type: 'GET',
    successField: 'data',
    onError: showServerError('Unable to look up duplicate contacts.'),
    toast: { showOnSuccess: false },
  });

  const previewEndpoint = useEndpoint<ApiResponseBase<MergePreview>, ErrorResponse>({
    type: 'GET',
    successField: 'data',
    onError: showServerError('Unable to load the combine preview.'),
    toast: { showOnSuccess: false },
  });

  const mergeEndpoint = useEndpoint<ApiResponseBase<MergeContactsResponse>, ErrorResponse>({
    type: 'POST',
    successField: 'data',
    onError: showServerError('Contacts were not able to be combined.'),
    toast: { showOnSuccess: false },
  });

  const getMergeCandidates = async (sourceContactId: number): Promise<MergeCandidate[]> => {
    candidatesEndpoint.setEndpointUrl(`/crm/contacts/${sourceContactId}/merge/candidates`);
    const response = await candidatesEndpoint.getResults();
    return response?.data?.candidates ?? [];
  };

  const getMergePreview = async (sourceContactId: number, selectedContactId: number): Promise<MergePreview | null> => {
    previewEndpoint.setEndpointUrl(`/crm/contacts/${sourceContactId}/merge/preview?selectedContactId=${selectedContactId}`);
    const response = await previewEndpoint.getResults();
    return response?.data ?? null;
  };

  const mergeContacts = async (sourceContactId: number, payload: MergePayload): Promise<MergeContactsResponse | null> => {
    mergeEndpoint.setEndpointUrl(`/crm/contacts/${sourceContactId}/merge`);
    const response = await mergeEndpoint.getResults({ data: payload });
    return response?.data ?? null;
  };

  return {
    getMergeCandidates,
    getMergePreview,
    isLoadingCandidates: candidatesEndpoint.isLoading,
    isLoadingPreview: previewEndpoint.isLoading,
    isMerging: mergeEndpoint.isLoading,
    mergeContacts,
  };
};
